import React from 'react';
import { Skeleton } from 'primereact/skeleton';

// Shared banner for ALL Borrower Report pages — V1 (soft pastel) design.
//
// Visual recipe (matches the prototype the team approved):
//   • Soft pastel gradient background (blue-50 → indigo-100 — theme variables,
//     not hex), so the banner sits quietly inside the page chrome.
//   • Dark title in text-900 (excellent AA contrast — no more white-on-purple)
//     with a primary-tinted icon.
//   • Sub-title in text-700.
//   • On the right: 2 small white credit cards with a coloured top-border
//     accent (green for Available, purple for Required) — readable at a glance
//     without shouting.
//
// Pure PrimeReact + PrimeFlex + PrimeIcons. No new CSS files, no embedded
// <style>, no hex codes (all colours come from theme variables). Skeleton
// placeholder during the credits fetch matches Lending Network's pattern.

const CreditBox = ({ icon, label, value, accent, loading }) => (
    <div
        className="surface-0 border-1 surface-border border-round p-2 text-center flex flex-column align-items-center justify-content-center min-w-7rem"
        style={{ borderTopWidth: '3px', borderTopColor: `var(--${accent}-500)` }}
    >
        <i className={`pi ${icon} text-${accent}-500 text-xs mb-1 block`} aria-hidden="true" />
        {loading ? (
            <Skeleton width="2.5rem" height="1.25rem" className="mb-1" />
        ) : (
            <span className={`text-${accent}-700 text-base font-bold line-height-1 mb-1 block`}>
                {value ?? 'N/A'}
            </span>
        )}
        <span className="text-xs font-medium text-700 line-height-1">{label}</span>
    </div>
);

const ReportBanner = ({
    icon,
    title,
    subtitle,
    availableCredits,
    requiredCredits,
    loading = false,
    showCredits = true,
}) => (
    <div
        className="border-round shadow-1 p-3 md:p-4 mb-4 flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3"
        style={{ background: 'linear-gradient(135deg, var(--blue-50) 0%, var(--indigo-100) 100%)' }}
        role="banner"
    >
        <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold text-900 m-0 mb-1">
                {icon && <i className={`pi ${icon} mr-2 text-primary`} aria-hidden="true" />}
                {title}
            </h1>
            {subtitle && (
                <p className="text-700 text-sm m-0">{subtitle}</p>
            )}
        </div>
        {showCredits && (
            <div className="flex flex-row align-items-stretch gap-2">
                <CreditBox icon="pi-wallet" label="Available Credits" value={availableCredits} accent="green" loading={loading} />
                <CreditBox icon="pi-tag" label="Credits Required" value={requiredCredits} accent="purple" loading={loading} />
            </div>
        )}
    </div>
);

export default ReportBanner;
